import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import PropTypes from "prop-types"
import { Formik, Form } from "formik"
import * as yup from "yup"
import { Modal, ModalBody } from "reactstrap"
import { useNavigate } from "react-router-dom"
import { toast, Bounce } from "react-toastify"
import CustomButton from "../OnboardingComponents/CustomButton"
import CustomInput from "../OnboardingComponents/CustomInput"
import { Avatar } from "../DashboardComponents/AvatarGroup"
import { ICONS } from "../../constants/icons"
import { patch, post } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { MODULE_META, MODULE_TO_API } from "../../pages/Flags/flagData"

const CreateFlagModalContext = createContext(null)

export const FLAG_REASONS = [
  { value: "harassment", label: "Harassment or inappropriate behavior" },
  { value: "copyright", label: "Copyright / Ownership Issue" },
  { value: "spam", label: "Spam or Misleading Information" },
  { value: "unsafe", label: "Unsafe Content" },
  { value: "fake_identity", label: "Fake Identity" },
  { value: "other", label: "Other" },
]

const MAX_IMAGE_BYTES = 5 * 1024 * 1024
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/gif"]

const validationSchema = yup.object().shape({
  reason: yup.string().required("Please select a reason"),
  details: yup
    .string()
    .trim()
    .when("reason", {
      is: "other",
      then: schema =>
        schema
          .required("Additional details are required")
          .min(10, "Please provide at least 10 characters"),
      otherwise: schema => schema.optional(),
    }),
  confirmed: yup
    .boolean()
    .oneOf([true], "Please confirm this report is accurate"),
  evidence: yup.mixed().nullable(),
})

const formatFileSize = bytes => {
  if (!bytes && bytes !== 0) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1).replace(/\.0$/, "")} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, "")} MB`
}

const matchReasonValue = text => {
  const hay = String(text || "").toLowerCase()
  const exact = FLAG_REASONS.find(item => item.label.toLowerCase() === hay)
  if (exact) return exact.value
  if (/harass|inappropriate/.test(hay)) return "harassment"
  if (/copyright|ownership|intellectual/.test(hay)) return "copyright"
  if (/spam|misleading/.test(hay)) return "spam"
  if (/unsafe|threat|violent|hate/.test(hay)) return "unsafe"
  if (/fake|impersonat|identity/.test(hay)) return "fake_identity"
  if (hay) return "other"
  return "harassment"
}

const acceptImageFile = (file, setFieldValue, setFieldError, setFieldTouched) => {
  setFieldTouched("evidence", true, false)
  if (!file) return
  if (!ACCEPTED_TYPES.includes(file.type)) {
    setFieldError("evidence", "Please upload a PNG, JPG, or GIF")
    return
  }
  if (file.size > MAX_IMAGE_BYTES) {
    setFieldError("evidence", "File must be 5MB or smaller")
    return
  }
  setFieldValue("evidence", file)
  setFieldError("evidence", undefined)
}

const appendArray = (formData, key, values = []) => {
  values.forEach(value => {
    if (value === undefined || value === null || value === "") return
    formData.append(`${key}[]`, value)
  })
}

const getReasonLabel = value =>
  FLAG_REASONS.find(item => item.value === value)?.label || value

const EvidenceFileRow = ({
  name,
  sizeLabel,
  previewSrc,
  onRemove,
}) => (
  <div className="create-flag-modal__file">
    <div className="create-flag-modal__file-meta">
      {previewSrc ? (
        <img
          className="create-flag-modal__file-thumb"
          src={previewSrc}
          alt=""
        />
      ) : (
        <span className="create-flag-modal__file-type">IMG</span>
      )}
      <span>
        <span className="create-flag-modal__file-name">{name}</span>
        {sizeLabel ? (
          <span className="create-flag-modal__file-size">{sizeLabel}</span>
        ) : null}
      </span>
    </div>
    <CustomButton variant="secondary" size="sm" type="button" onClick={onRemove}>
      Remove
    </CustomButton>
  </div>
)

const SelectedEvidenceRow = ({ file, onRemove }) => {
  const [previewSrc, setPreviewSrc] = useState("")

  useEffect(() => {
    if (!(file instanceof File)) {
      setPreviewSrc("")
      return undefined
    }
    const url = URL.createObjectURL(file)
    setPreviewSrc(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  return (
    <EvidenceFileRow
      name={file.name}
      sizeLabel={formatFileSize(file.size)}
      previewSrc={previewSrc}
      onRemove={onRemove}
    />
  )
}

const buildFlagFormData = ({
  isEdit,
  values,
  moduleKey,
  flaggableId,
}) => {
  const formData = new FormData()
  const reasonLabel = getReasonLabel(values.reason)
  const additionalDetails = String(values.details || "").trim()

  // Always send selected reason + additional details (every reason type).
  appendArray(formData, "reasons", [reasonLabel])
  formData.append("description", additionalDetails)

  if (!isEdit) {
    formData.append("module", MODULE_TO_API[moduleKey] || "profile")
    formData.append("flaggable_id", String(flaggableId))
  } else {
    appendArray(formData, "remove_media_ids", values.removedMediaIds || [])
  }

  if (values.evidence instanceof File) {
    formData.append("medias[]", values.evidence)
  }

  return formData
}

const CreateFlagModal = ({
  isOpen,
  onClose,
  entity = {},
  flag = null,
  onSuccess,
  navigateOnSuccess = true,
}) => {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const isEdit = Boolean(flag?.id)

  const moduleKey = entity.moduleKey || flag?.moduleKey || "member_profile"
  const moduleLabel =
    entity.moduleLabel ||
    flag?.moduleLabel ||
    MODULE_META[moduleKey]?.label ||
    "Member Profile"
  const name = entity.name || flag?.name || "Unknown"
  const avatar = entity.avatar || flag?.avatar
  const flaggableId =
    entity.flaggableId || entity.userId || flag?.flaggableId || flag?.userId

  const initialValues = useMemo(
    () => ({
      reason: matchReasonValue(flag?.reason),
      details: flag?.description || "",
      confirmed: false,
      evidence: null,
      existingMedia: Array.isArray(flag?.evidence) ? flag.evidence : [],
      removedMediaIds: [],
    }),
    [flag],
  )

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!isEdit && !flaggableId) {
      toast.error("Unable to submit flag. Missing reported entity.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
      setSubmitting(false)
      return
    }

    try {
      const formData = buildFlagFormData({
        isEdit,
        values,
        moduleKey,
        flaggableId,
      })

      const response = isEdit
        ? await patch(endpoints.updateFlag(flag.id), formData)
        : await post(endpoints.createFlag, formData)

      if (!response?.data?.success) return

      toast.success(
        response.data.message || (isEdit ? "Flag updated." : "Flag submitted."),
        {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        },
      )

      if (typeof onSuccess === "function") onSuccess()
      onClose()
      if (!isEdit && navigateOnSuccess) navigate("/flags")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--create-flag"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body create-flag-modal__body">
        <Formik
          key={isOpen ? `create-flag-${flag?.id || flaggableId || "new"}` : "closed"}
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            setFieldError,
            setFieldTouched,
            isSubmitting,
          }) => (
            <Form className="create-flag-modal">
              <div className="create-flag-modal__main">
                <div className="create-flag-modal__header">
                  <h2 className="create-flag-modal__title">
                    {isEdit ? "Edit Flag" : "Create Flag"}
                  </h2>
                  <p className="create-flag-modal__subtitle">
                    Help us keep the community safe and respectful.
                  </p>
                </div>

                <div className="create-flag-modal__entity">
                  <p className="create-flag-modal__entity-label">
                    Reported Entity
                  </p>
                  <div className="create-flag-modal__entity-row">
                    <div className="create-flag-modal__entity-meta">
                      <Avatar src={avatar} name={name} size="sm" />
                      <span className="create-flag-modal__entity-name">
                        {name}
                      </span>
                    </div>
                    <span className="create-flag-modal__module-badge">
                      {moduleLabel}
                    </span>
                  </div>
                </div>

                <fieldset className="create-flag-modal__reasons">
                  <legend className="create-flag-modal__label">
                    Why are you flagging this? (Select one)
                  </legend>
                  <div className="create-flag-modal__reason-list" role="radiogroup">
                    {FLAG_REASONS.map(item => {
                      const selected = values.reason === item.value
                      return (
                        <button
                          key={item.value}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          className={`create-flag-modal__reason${
                            selected ? " create-flag-modal__reason--selected" : ""
                          }`}
                          onClick={() => {
                            setFieldValue("reason", item.value)
                            setFieldTouched("details", false, false)
                            setFieldError("details", undefined)
                          }}
                        >
                          <img
                            src={
                              selected
                                ? ICONS.flagCheckRectChecked
                                : ICONS.flagCheckRect
                            }
                            alt=""
                            width={16}
                            height={16}
                          />
                          <span>{item.label}</span>
                        </button>
                      )
                    })}
                  </div>
                  {touched.reason && errors.reason ? (
                    <p className="amok-field__message amok-field__message--error">
                      {errors.reason}
                    </p>
                  ) : null}
                </fieldset>

                <CustomInput
                  textarea
                  label={
                    values.reason === "other"
                      ? "Additional Details (Required)"
                      : "Additional Details"
                  }
                  name="details"
                  rows={4}
                  value={values.details}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Please describe what happened in as much detail as possible..."
                  error={
                    touched.details && errors.details ? errors.details : ""
                  }
                  className="create-flag-modal__details"
                />

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif"
                  className="create-flag-modal__upload-input"
                  onChange={event => {
                    const file = event.target.files?.[0]
                    acceptImageFile(
                      file,
                      setFieldValue,
                      setFieldError,
                      setFieldTouched,
                    )
                    event.target.value = ""
                  }}
                />

                {values.existingMedia
                  .filter(file => !values.removedMediaIds.includes(file.id))
                  .map(file => (
                    <EvidenceFileRow
                      key={file.id}
                      name={file.name}
                      sizeLabel={
                        typeof file.size === "number"
                          ? formatFileSize(file.size)
                          : file.size
                      }
                      previewSrc={file.thumbnail || file.url}
                      onRemove={() =>
                        setFieldValue("removedMediaIds", [
                          ...values.removedMediaIds,
                          file.id,
                        ])
                      }
                    />
                  ))}

                {values.evidence ? (
                  <SelectedEvidenceRow
                    file={values.evidence}
                    onRemove={() => {
                      setFieldValue("evidence", null)
                      setFieldError("evidence", undefined)
                    }}
                  />
                ) : (
                  <div
                    className={`create-flag-modal__upload${
                      errors.evidence ? " create-flag-modal__upload--error" : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault()
                        fileInputRef.current?.click()
                      }
                    }}
                    onDragOver={event => {
                      event.preventDefault()
                      event.stopPropagation()
                    }}
                    onDrop={event => {
                      event.preventDefault()
                      event.stopPropagation()
                      const file = event.dataTransfer?.files?.[0]
                      acceptImageFile(
                        file,
                        setFieldValue,
                        setFieldError,
                        setFieldTouched,
                      )
                    }}
                  >
                    <span className="create-flag-modal__upload-icon">
                      <img src={ICONS.flagExport} alt="" width={16} height={16} />
                    </span>
                    <p className="create-flag-modal__upload-title">
                      Click to upload or drag and drop
                    </p>
                    <p className="create-flag-modal__upload-hint">
                      PNG, JPG, GIF (max 5MB)
                    </p>
                    {errors.evidence ? (
                      <p className="amok-field__message amok-field__message--error">
                        {errors.evidence}
                      </p>
                    ) : null}
                  </div>
                )}

                <button
                  type="button"
                  className={`create-flag-modal__confirm${
                    values.confirmed ? " create-flag-modal__confirm--checked" : ""
                  }`}
                  onClick={() => {
                    setFieldValue("confirmed", !values.confirmed)
                    setFieldTouched("confirmed", true, false)
                  }}
                >
                  <img
                    src={
                      values.confirmed
                        ? ICONS.flagCheckRectChecked
                        : ICONS.flagCheckRect
                    }
                    alt=""
                    width={16}
                    height={16}
                  />
                  <span>
                    I confirm that this report is accurate to the best of my
                    knowledge.
                  </span>
                </button>
                {touched.confirmed && errors.confirmed ? (
                  <p className="amok-field__message amok-field__message--error">
                    {errors.confirmed}
                  </p>
                ) : null}
              </div>

              <div className="create-flag-modal__footer">
                <CustomButton
                  variant="danger"
                  size="md"
                  type="button"
                  className="create-flag-modal__cancel"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="primary"
                  size="md"
                  type="submit"
                  loading={isSubmitting}
                >
                  {isEdit ? "Save Changes" : "Submit Flag"}
                </CustomButton>
              </div>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  )
}

CreateFlagModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  entity: PropTypes.shape({
    name: PropTypes.string,
    avatar: PropTypes.string,
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    flaggableId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    moduleKey: PropTypes.string,
    moduleLabel: PropTypes.string,
  }),
  flag: PropTypes.object,
  onSuccess: PropTypes.func,
  navigateOnSuccess: PropTypes.bool,
}

export const CreateFlagModalProvider = ({ children }) => {
  const [state, setState] = useState({
    isOpen: false,
    entity: null,
    flag: null,
    onSuccess: null,
    navigateOnSuccess: true,
  })

  const openCreateFlag = useCallback((entity = {}, flag = null, options = {}) => {
    setState({
      isOpen: true,
      entity,
      flag,
      onSuccess: options.onSuccess || null,
      navigateOnSuccess: options.navigateOnSuccess !== false,
    })
  }, [])

  const closeCreateFlag = useCallback(() => {
    setState({
      isOpen: false,
      entity: null,
      flag: null,
      onSuccess: null,
      navigateOnSuccess: true,
    })
  }, [])

  const value = useMemo(
    () => ({ openCreateFlag, closeCreateFlag }),
    [openCreateFlag, closeCreateFlag],
  )

  return (
    <CreateFlagModalContext.Provider value={value}>
      {children}
      <CreateFlagModal
        isOpen={state.isOpen}
        onClose={closeCreateFlag}
        entity={state.entity || {}}
        flag={state.flag}
        onSuccess={state.onSuccess}
        navigateOnSuccess={state.navigateOnSuccess}
      />
    </CreateFlagModalContext.Provider>
  )
}

CreateFlagModalProvider.propTypes = {
  children: PropTypes.node,
}

export const useCreateFlagModal = () => {
  const context = useContext(CreateFlagModalContext)
  if (!context) {
    throw new Error(
      "useCreateFlagModal must be used within CreateFlagModalProvider",
    )
  }
  return context
}

export default CreateFlagModal
